import type { GuideLang } from "@/lib/virtualspace/guide/guide-public-shape";

/**
 * Whether a venue is open right now, from its opening hours (Cairo time).
 * Hours are {"mon": [["09:00","17:00"]], "fri": []}: [] is closed, a
 * missing day is unknown. A range ending before it starts runs past
 * midnight ("19:00"-"02:00"). Pure.
 */

const ZONE = "Africa/Cairo";
const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
type Day = (typeof DAYS)[number];
export type Hours = Partial<Record<string, Array<[string, string]>>>;

export type OpenState =
  | { known: false }
  | { known: true; open: true; until: string }
  | { known: true; open: false; next: { day: Day; at: string } | null };

function cairoNow(now: Date): { day: Day; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", { timeZone: ZONE, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const day = get("weekday").slice(0, 3).toLowerCase() as Day;
  return { day, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

const toMinutes = (clock: string) => {
  const [h, m] = clock.split(":").map(Number);
  return h * 60 + m;
};

/** The venue's state at `now`: open until a time, closed until the next opening, or unknown. */
export function openState(hours: Hours | null | undefined, now: Date): OpenState {
  if (!hours || Object.keys(hours).length === 0) return { known: false };
  const { day, minutes } = cairoNow(now);
  const index = DAYS.indexOf(day);
  const yesterday = DAYS[(index + 6) % 7];
  // Open now: a range of today containing the minute, or yesterday's range running past midnight.
  for (const [from, to] of hours[day] ?? []) {
    const start = toMinutes(from);
    const end = to === "24:00" ? 1440 : toMinutes(to);
    if (end > start ? minutes >= start && minutes < end : minutes >= start) return { known: true, open: true, until: to };
  }
  for (const [from, to] of hours[yesterday] ?? []) {
    const start = toMinutes(from);
    const end = toMinutes(to);
    if (end < start && minutes < end) return { known: true, open: true, until: to };
  }
  // Closed: the next opening within a week.
  for (let ahead = 0; ahead < 7; ahead++) {
    const d = DAYS[(index + ahead) % 7];
    const ranges = hours[d];
    if (!ranges || ranges.length === 0) continue;
    for (const [from] of [...ranges].sort((a, b) => toMinutes(a[0]) - toMinutes(b[0]))) {
      if (ahead === 0 && toMinutes(from) <= minutes) continue;
      return { known: true, open: false, next: { day: d, at: from } };
    }
  }
  return { known: true, open: false, next: null };
}

const DAY_NAMES: Record<GuideLang, Record<Day, string>> = {
  en: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
  ar: { mon: "الإثنين", tue: "الثلاثاء", wed: "الأربعاء", thu: "الخميس", fri: "الجمعة", sat: "السبت", sun: "الأحد" },
};

/** "Open now · until 16:00", "Closed now · opens Tuesday 09:00", or "" when hours are unknown. */
export function openLabel(state: OpenState, lang: GuideLang, now: Date): string {
  if (!state.known) return "";
  if (state.open) return lang === "ar" ? `مفتوح الآن · حتى ${state.until}` : `Open now · until ${state.until}`;
  if (!state.next) return lang === "ar" ? "مغلق الآن" : "Closed now";
  const today = cairoNow(now).day;
  const when = state.next.day === today ? "" : `${DAY_NAMES[lang][state.next.day]} `;
  return lang === "ar" ? `مغلق الآن · يفتح ${when}${state.next.at}` : `Closed now · opens ${when}${state.next.at}`;
}
